import React, { useEffect, ReactNode } from 'react';
import { FmlxButton, FmlxModal } from 'fmlx-common-ui';

export interface RMModalProps {
  showModal: boolean;
  setShowModal: (flag: boolean) => void;
  size?: FmlxModal.Size;
  onOk?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  body: any;
  heading?: string;
  saveButtonText?: string;
  okButtonText?: string;
  noButtonText?: string;
  isOkOnly?: boolean;
  isCancelOnly?: boolean;
  isSaveAvailable?: boolean;
  icon?: any;
  titleClr?: string;
  okayDisabled?: boolean;
  saveDisabled?: boolean;
  showButtons?: boolean;
  dialogClassName?: string;
  scrollable?: boolean;
  hideFooter?: boolean;
  showFooterSeparator?: boolean;
  headerClassName?: string;
  headerStyle?: React.CSSProperties;
  onHide?: () => void;
  hideCloseButton?: boolean;
  customHeader?: ReactNode;
  isModalEscapeable?: boolean;
  id?: string;
  inlineMessage?: {
    type: 'error' | 'warning';
    message: string;
  };
  customFooter?: ReactNode;
}

function RMModal(props: RMModalProps) {
  const {
    showModal,
    setShowModal,
    size = FmlxModal.Size.MEDIUM,
    onOk = () => {},
    onCancel = () => {
      setShowModal(false);
    },
    onSave = () => {},
    body,
    heading,
    saveButtonText = 'Save',
    okButtonText = 'Yes, Proceed',
    noButtonText = 'Cancel',
    isOkOnly = false,
    isCancelOnly = false,
    isSaveAvailable = false,
    okayDisabled = false,
    saveDisabled = false,
    showButtons = true,
    hideCloseButton = false,
    onHide = () => {
      setShowModal(false);
    },
    customHeader,
    isModalEscapeable = true,
    id,
    inlineMessage,
    customFooter,
  } = props;

  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && showModal) {
      onOk();
    }
  };

  useEffect(() => {
    if (onOk && showButtons) {
      document.addEventListener('keydown', keyDownHandler);
      return () => {
        document.removeEventListener('keydown', keyDownHandler);
      };
    }
  }, [showModal, onOk, showButtons]);

  // commented for future reference: this is for the custom footer, in case we need custom footer just uncomment the following
  // const customFooter = showButtons ? (
  //   <div style={style.footer}>
  //     <>
  //       {isSaveAvailable && (
  //         <RMButton
  //           disabled={saveDisabled}
  //           customStyle={{ backgroundColor: '#F7A246', width: '9.5rem' }}
  //           onClick={onSave || (() => {})} // TODO: this is temp. needs to be fixed.
  //           label={saveButtonText}
  //         />
  //       )}
  //       {!isCancelOnly && (
  //         <RMButton
  //           disabled={okayDisabled}
  //           customStyle={{ backgroundColor: '#F7A246', width: '9.5rem' }}
  //           onClick={onOk || (() => {})} // TODO: this is temp. needs to be fixed.
  //           label={okButtonText}
  //         />
  //       )}
  //       {!isOkOnly && (
  //         <RMButton
  //           className="btn-outline-yellow-wide"
  //           onClick={() => onCancel()}
  //           label={noButtonText}
  //         />
  //       )}
  //     </>
  //   </div>
  // ) : <div></div>;

  return (
    <FmlxModal
      id={id ?? `rmmodal-${heading}-wrapper`}
      open={showModal}
      onCloseClick={onHide}
      title={heading}
      size={size}
      hideCloseButton={hideCloseButton}
      customFooter={props.hideFooter === true && !customFooter ? <area /> : customFooter}
      primaryButton={{
        show: showButtons && isSaveAvailable,
        disabled: saveDisabled,
        label: saveButtonText,
        type: FmlxButton.Type.PRIMARY,
        variant: FmlxButton.Variant.CONTAIN,
        onClick: onSave || (() => {}),
      }}
      secondaryButton={{
        show: showButtons && !isCancelOnly,
        disabled: okayDisabled,
        label: okButtonText,
        type: FmlxButton.Type.PRIMARY,
        variant: FmlxButton.Variant.CONTAIN,
        onClick: onOk || (() => {}),
      }}
      tertiaryButton={{
        show: showButtons && !isOkOnly,
        disabled: false,
        label: noButtonText,
        type: FmlxButton.Type.PRIMARY,
        variant: FmlxButton.Variant.OUTLINE,
        onClick: () => onCancel(),
      }}
      customHeader={customHeader}
      disableBlur={!isModalEscapeable}
      inlineMessage={inlineMessage}
    >
      <div id={`rmmodal-body-${body}`}>{body}</div>
    </FmlxModal>
  );
}

export default RMModal;
